import { useState, useEffect, useCallback, useMemo } from 'react';

import Util from '../util/util';

/**
 * Valid chip types that can be fetched from the organization configuration.
 */
export const CHIP_TYPES = {
  KPI: 'KPI',
  LOCATION: 'Location',
  HRO: 'HRO',
};

/**
 * Custom hook for fetching and managing organization configuration chips (KPIs, Locations, etc.).
 *
 * Consolidates the getValidChips pattern that was duplicated in:
 * - SubmitReport.js
 * - AdminReportDetails.js
 * - AdminOrganizationDashboard.js
 *
 * @param {string} organization - The organization to fetch chips for
 * @param {string} chipType - The type of chips to fetch (use CHIP_TYPES constants)
 * @param {Object} options - Optional configuration
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {string} options.fallbackOrganization - Fallback organization if primary is empty
 *
 * @returns {Object} Chip data and utilities
 *
 * @example
 * const { chips, loading, error, refetch } = useValidChips(organization, CHIP_TYPES.KPI);
 *
 * // Or fetch multiple types
 * const kpiChips = useValidChips(organization, CHIP_TYPES.KPI);
 * const locationChips = useValidChips(organization, CHIP_TYPES.LOCATION);
 */
const useValidChips = (organization, chipType, options = {}) => {
  const { autoFetch = true, fallbackOrganization = null } = options;

  const [chips, setChips] = useState([]);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);

  const effectiveOrganization = organization || fallbackOrganization;

  const fetchChips = useCallback(async () => {
    if (!effectiveOrganization || !chipType) {
      setChips([]);
      setLoading(false);
      return [];
    }

    setLoading(true);
    setError(null);

    const requestData = {
      queryParams: {
        organization: effectiveOrganization,
        value_type: chipType,
      },
    };

    try {
      const data = await Util.get(
        'Resty',
        '/items/organization_configuration/value_type',
        requestData,
      );

      // Sort chips by value alphabetically (case-insensitive)
      const sortedChips = (data.items || []).sort((a, b) => {
        const upperA = a.value.toUpperCase();
        const upperB = b.value.toUpperCase();

        if (upperA < upperB) return -1;
        if (upperA > upperB) return 1;
        return 0;
      });

      setChips(sortedChips);
      setLoading(false);
      return sortedChips;
    } catch (err) {
      Util.printAPIError(err);
      setError(err);
      setChips([]);
      setLoading(false);
      return [];
    }
  }, [effectiveOrganization, chipType]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (autoFetch && effectiveOrganization && chipType) {
      fetchChips();
    }
  }, [autoFetch, effectiveOrganization, chipType, fetchChips]);

  // Extract just the values (common use case). Memoized so the returned
  // array identity is stable across renders — consumers use chipValues as
  // a useEffect dependency, and a fresh array each render causes infinite
  // re-render loops (e.g. SubmitReport in QR mode with a location param).
  const chipValues = useMemo(() => chips.map((chip) => chip.value), [chips]);

  // Helper to check if a value exists in chips
  const hasChip = useCallback(
    (value) => chipValues.some((v) => v.toLowerCase() === value.toLowerCase()),
    [chipValues],
  );

  return {
    // Data
    chips, // Full chip objects with metadata
    chipValues, // Just the value strings
    loading,
    error,

    // Actions
    refetch: fetchChips,
    setChips, // For manual updates if needed

    // Helpers
    hasChip,
  };
};

export default useValidChips;
